'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

/**
 * Adds a subtle 3D tilt-on-hover to portfolio and service cards. Re-attaches on
 * every route change. Only runs on hover-capable devices and respects
 * reduced-motion. No JSX — it wires behaviour onto existing cards.
 */
export default function CardTilt() {
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(hover: hover)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const MAX = 7; // degrees
    const cleanups: Array<() => void> = [];

    const attach = () => {
      const cards = Array.from(
        document.querySelectorAll<HTMLElement>('.service-card, .portfolio-card')
      );
      cards.forEach((el) => {
        const onMove = (e: MouseEvent) => {
          const r = el.getBoundingClientRect();
          const px = (e.clientX - r.left) / r.width - 0.5;
          const py = (e.clientY - r.top) / r.height - 0.5;
          el.style.transition = 'transform 0.08s ease-out';
          el.style.transform = `perspective(900px) rotateX(${(-py * MAX).toFixed(2)}deg) rotateY(${(px * MAX).toFixed(2)}deg) translateY(-4px)`;
        };
        const onLeave = () => {
          el.style.transition = 'transform 0.45s ease';
          el.style.transform = '';
        };
        el.addEventListener('mousemove', onMove);
        el.addEventListener('mouseleave', onLeave);
        cleanups.push(() => {
          el.removeEventListener('mousemove', onMove);
          el.removeEventListener('mouseleave', onLeave);
          el.style.transform = '';
          el.style.transition = '';
        });
      });
    };

    // Wait a frame so freshly-navigated route content is in the DOM.
    const raf = requestAnimationFrame(attach);
    return () => {
      cancelAnimationFrame(raf);
      cleanups.forEach((c) => c());
    };
  }, [pathname]);

  return null;
}
